import React from 'react';

import styled from 'styled-components';

import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS } from 'components/View/constants';

import { useWidgetContext } from './WidgetContext';
import Dropdown from '../../../components/lib/Dropdown';
import DownCaretIcon from '../../Icons/DownCaretIcon';
import { WIDGET_FILTER_OPTIONS, WIDGET_FILTER_TYPE_LABEL, WIDGET_FILTER_TYPES, WIDGET_FILTERS, WIDGET_TYPES_LABELS  } from '../constants';
import { slugifyWidgetText } from '../utils';

const StyledDropdownMenu = styled.div`
  .ant-dropdown-menu {
    padding: 0;
    border-radius: 0;
    border: 1px solid var(--line-gray);
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    width: 155px;
    max-height: 160px;
    overflow-y: auto;

    .ant-dropdown-menu-item {
      font-size: 12px;
      pointer-events: none;
    }
  }
`;

const Label = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 2px;
`;

interface Props {
  widgetType: number;
  filterType: number;
  selection: number[];
  options?: any[];
}

const getFilterLabelTestId = (filterType: number): string | undefined => {
  const filterLabel = WIDGET_FILTER_TYPE_LABEL[filterType]?.[1];
  if (!filterLabel) {
    return undefined;
  }
  return `widget-filter-${slugifyWidgetText(filterLabel)}`;
};

const WidgetFiltersInfoLabel = ({ widgetType, filterType, selection, options }: Props) => {
  const { filters } = useWidgetContext();

  if (!WIDGET_FILTERS[widgetType]?.includes(filterType)) return null;

  const filterOptions = options ?? WIDGET_FILTER_OPTIONS[filterType];

  const filterLabelTestId = getFilterLabelTestId(filterType);

  let countLabelText = `All ${WIDGET_FILTER_TYPE_LABEL[filterType][1]}`;

  if (selection?.length === 1) {
    const selectedItem = filterOptions?.find((item) => item.id === selection[0]);
    countLabelText = selectedItem?.name ?? '';
  } else if (selection?.length > 1 && selection?.length < filterOptions.length) {
    countLabelText = `${selection?.length} ${WIDGET_FILTER_TYPE_LABEL[filterType][1]}`;
  }

  const items = (filterOptions || [])
    .filter((option: any) => selection?.includes(option.id))
    .map((item: any) => ({
      key: item.id,
      label: item.name,
    }));

  const widgetTestId = (WIDGET_TYPES_LABELS[widgetType] ?? '').toLowerCase().replace(/\s+/g, '-');
  const filterTestId = WIDGET_FILTER_TYPE_LABEL[filterType]?.[0].toLowerCase().replace(/\s+/g, '-');

  const dataTestId = `${widgetTestId}-widget-${filterTestId}`;

  return (
    <>
      <Dropdown
        menu={{ items }}
        dropdownRender={(menu) => (
          <StyledDropdownMenu>
            {React.cloneElement(menu as React.ReactElement<any>, {
              className: `mini-scroll`,
            })}
          </StyledDropdownMenu>
        )}
        disabled={selection?.length < 2}
        placement={'bottomRight'}
      >
        <Label
          data-test-id={filterLabelTestId}
          style={{ cursor: selection?.length > 1 ? 'pointer' : 'default' }}
        >
          {countLabelText}
          {selection?.length > 1 && <DownCaretIcon />}
          {filterType == WIDGET_FILTER_TYPES.ROOM_TYPE &&
            `(${AGGREGATION_TYPE_LABELS[filters.roomTypesPriceDisplay] || AGGREGATION_TYPE_LABELS[AGGREGATION_TYPE.AVG]
            })`}
          {filterType == WIDGET_FILTER_TYPES.CARE_LEVEL &&
            `(${AGGREGATION_TYPE_LABELS[filters.careLevelsStatsType] || AGGREGATION_TYPE_LABELS[AGGREGATION_TYPE.AVG]
            })`}
          {filterType == WIDGET_FILTER_TYPES.MEDICATION_LEVEL &&
            `(${AGGREGATION_TYPE_LABELS[filters.medicationLevelsStatsType] ||
            AGGREGATION_TYPE_LABELS[AGGREGATION_TYPE.AVG]
            })`}
        </Label>
      </Dropdown>
      <span>·</span>
    </>
  );
};

export default WidgetFiltersInfoLabel;
